/**
 * useVoiceInput Hook
 * ===================
 * Hook for voice input functionality using Web Speech API.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';

// =============================================================================
// TYPES
// =============================================================================

interface VoiceInputState {
  isListening: boolean;
  isProcessing: boolean;
  transcript: string;
  interimTranscript: string;
  error: string | null;
  isSupported: boolean;
}

interface VoiceInputConfig {
  continuous?: boolean;
  interimResults?: boolean;
  language?: string;
  onResult?: (transcript: string) => void;
  onInterimResult?: (transcript: string) => void;
  onError?: (error: string) => void;
}

interface ParsedPerioReading {
  toothNumber: number;
  readings: {
    site: string;
    pocketDepth?: number;
    bleeding?: boolean;
  }[];
}

interface ParsedClinicalNotes {
  type: 'clinical_notes';
  notes: string;
}

interface ParsedAAPClassification {
  type: 'aap_classification';
  stage?: string;
  grade?: string;
  extent?: string;
}

interface ParsedRiskFactor {
  type: 'risk_factor';
  factor: 'smoking' | 'diabetes' | 'genetics' | 'stress' | 'medications' | 'poor_nutrition';
  enabled: boolean;
}

interface ParsedReferral {
  type: 'referral';
  recommended: boolean;
  reason?: string;
}

interface ParsedToothStatus {
  type: 'tooth_status';
  toothNumber: number;
  status: 'present' | 'missing' | 'implant' | 'crown' | 'pontic' | 'partially_impacted' | 'fully_impacted';
}

interface ParsedMobility {
  type: 'mobility';
  toothNumber: number;
  mobility: number | null;
}

interface ParsedFurcation {
  type: 'furcation';
  toothNumber: number;
  furcation: number | null;
}

export type ParsedVoiceData = 
  | (ParsedPerioReading & { type: 'site_readings' })
  | ParsedClinicalNotes
  | ParsedAAPClassification
  | ParsedRiskFactor
  | ParsedReferral
  | ParsedToothStatus
  | ParsedMobility
  | ParsedFurcation;

// =============================================================================
// HOOK
// =============================================================================

export function useVoiceInput(config: VoiceInputConfig = {}) {
  const {
    continuous = true,
    interimResults = true,
    language = 'en-US',
    onResult,
    onInterimResult,
    onError,
  } = config;

  const [state, setState] = useState<VoiceInputState>({
    isListening: false,
    isProcessing: false,
    transcript: '',
    interimTranscript: '',
    error: null,
    isSupported: typeof window !== 'undefined' && 'webkitSpeechRecognition' in window,
  });

  const recognitionRef = useRef<any>(null);
  const finalTranscriptRef = useRef('');
  const isIntentionalStopRef = useRef(false);
  const lastStartTimeRef = useRef(0);
  
  // Store callbacks in refs to avoid re-initializing recognition
  const onResultRef = useRef(onResult);
  const onInterimResultRef = useRef(onInterimResult);
  const onErrorRef = useRef(onError);
  
  // Update callback refs when they change
  useEffect(() => {
    onResultRef.current = onResult;
    onInterimResultRef.current = onInterimResult;
    onErrorRef.current = onError;
  }, [onResult, onInterimResult, onError]);

  // Initialize speech recognition
  useEffect(() => {
    console.log('[Voice Input] ========== INITIALIZATION ==========');
    console.log('[Voice Input] Window available:', typeof window !== 'undefined');
    
    if (typeof window === 'undefined') {
      console.log('[Voice Input] No window - server-side rendering');
      return;
    }

    const SpeechRecognition = (window as any).webkitSpeechRecognition || (window as any).SpeechRecognition;
    console.log('[Voice Input] SpeechRecognition available:', !!SpeechRecognition);
    
    if (!SpeechRecognition) {
      console.log('[Voice Input] Speech recognition not supported');
      setState((prev) => ({ ...prev, isSupported: false }));
      return;
    }

    console.log('[Voice Input] Creating recognition object');
    const recognition = new SpeechRecognition();
    recognition.continuous = continuous;
    recognition.interimResults = interimResults;
    recognition.lang = language;
    console.log('[Voice Input] Recognition configured:', { continuous, interimResults, language });

    recognition.onstart = () => {
      console.log('[Voice Input] Recognition started (onstart event)');
      lastStartTimeRef.current = Date.now();
      isIntentionalStopRef.current = false;
      setState((prev) => ({
        ...prev,
        isListening: true,
        error: null,
      }));
    };

    recognition.onend = () => {
      const runTime = Date.now() - lastStartTimeRef.current;
      console.log('[Voice Input] Recognition ended (onend event)', {
        wasIntentional: isIntentionalStopRef.current,
        runTime: runTime + 'ms'
      });
      
      // Normal behavior: recognition can start/end while waiting for speech
      // Only intentional stops should prevent auto-restart
      setState((prev) => ({
        ...prev,
        isListening: false,
      }));
    };

    recognition.onerror = (event: any) => {
      console.log('[Voice Input] Recognition error:', event.error);
      
      // Some errors are normal in continuous mode and should not stop recognition
      const nonCriticalErrors = ['no-speech', 'aborted'];
      
      if (nonCriticalErrors.includes(event.error)) {
        console.log('[Voice Input] Non-critical error, will auto-restart:', event.error);
        // Don't set error state for these - just let recognition restart
        setState((prev) => ({
          ...prev,
          isListening: false,
        }));
        return;
      }
      
      // Critical errors that need user intervention
      let errorMessage = 'Voice recognition error';
      
      switch (event.error) {
        case 'not-allowed':
          errorMessage = 'Microphone access denied. Please enable microphone permissions.';
          break;
        case 'audio-capture':
          errorMessage = 'No microphone detected.';
          break;
        case 'network':
          errorMessage = 'Network error. Please check your connection.';
          break;
        default:
          errorMessage = `Voice recognition error: ${event.error}`;
      }

      setState((prev) => ({
        ...prev,
        isListening: false,
        error: errorMessage,
      }));
      onErrorRef.current?.(errorMessage);
    };

    recognition.onresult = (event: any) => {
      let interim = '';
      let final = finalTranscriptRef.current;

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + ' ';
          finalTranscriptRef.current = final;
          onResultRef.current?.(transcript.trim());
        } else {
          interim += transcript;
        }
      }

      setState((prev) => ({
        ...prev,
        transcript: final.trim(),
        interimTranscript: interim,
      }));
      
      if (interim) {
        onInterimResultRef.current?.(interim);
      }
    };

    recognitionRef.current = recognition;
    console.log('[Voice Input] Recognition object stored in ref');
    console.log('[Voice Input] Initialization complete');

    // Cleanup function - only runs when component unmounts or dependencies change
    return () => {
      console.log('[Voice Input] Cleanup function called - component unmounting or config changed');
      try {
        recognition.stop();
      } catch (e) {
        console.log('[Voice Input] Cleanup error (safe to ignore):', e);
      }
    };
  }, [continuous, interimResults, language]);

  // Start listening
  const startListening = useCallback(() => {
    console.log('[Voice Input] ========== startListening CALLED ==========');
    console.log('[Voice Input] State:', {
      isSupported: state.isSupported,
      isListening: state.isListening,
      hasRecognition: !!recognitionRef.current,
      hasError: !!state.error,
      error: state.error
    });
    
    if (!state.isSupported) {
      console.log('[Voice Input] BLOCKED - Not supported');
      toast.error('Voice input is not supported in this browser');
      return;
    }

    if (!recognitionRef.current) {
      console.log('[Voice Input] BLOCKED - No recognition object');
      return;
    }

    if (state.isListening) {
      console.log('[Voice Input] BLOCKED - Already listening');
      return;
    }

    console.log('[Voice Input] All checks passed - proceeding with start');
    finalTranscriptRef.current = '';
    isIntentionalStopRef.current = false;
    setState((prev) => ({
      ...prev,
      transcript: '',
      interimTranscript: '',
      error: null, // Clear any previous errors when starting
    }));
    
    try {
      console.log('[Voice Input] Calling recognition.start()');
      recognitionRef.current.start();
    } catch (error: any) {
      console.log('[Voice Input] Start error (might already be started):', error.message);
      // Already started or other error
    }
  }, [state.isSupported, state.isListening]);

  // Stop listening
  const stopListening = useCallback(() => {
    console.log('[Voice Input] ========== stopListening CALLED ==========');
    console.log('[Voice Input] State:', { 
      hasRecognition: !!recognitionRef.current,
      isListening: state.isListening 
    });
    
    // Mark as intentional stop to prevent error state
    isIntentionalStopRef.current = true;
    
    // Update state immediately - don't wait for onend event
    console.log('[Voice Input] Setting isListening to false');
    setState((prev) => ({
      ...prev,
      isListening: false,
    }));
    
    if (recognitionRef.current) {
      try {
        console.log('[Voice Input] Calling recognition.stop()');
        recognitionRef.current.stop();
        console.log('[Voice Input] Recognition stop() called successfully');
      } catch (error: any) {
        console.error('[Voice Input] Error stopping recognition:', error);
      }
    } else {
      console.warn('[Voice Input] Cannot stop - no recognition object');
    }
  }, [state.isListening]);

  // Toggle listening
  const toggleListening = useCallback(() => {
    if (state.isListening) {
      stopListening();
    } else {
      startListening();
    }
  }, [state.isListening, startListening, stopListening]);

  // Clear transcript
  const clearTranscript = useCallback(() => {
    finalTranscriptRef.current = '';
    setState((prev) => ({
      ...prev,
      transcript: '',
      interimTranscript: '',
    }));
  }, []);

  return {
    ...state,
    startListening,
    stopListening,
    toggleListening,
    clearTranscript,
  };
}

// =============================================================================
// PARSING UTILITIES
// =============================================================================

/**
 * Master parser that routes to appropriate parsing function
 */
export function parseVoiceInput(transcript: string): ParsedVoiceData | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Check for clinical notes
  if (normalized.includes('clinical notes') || normalized.includes('notes:')) {
    return parseClinicalNotes(transcript);
  }
  
  // Check for AAP classification
  if (normalized.includes('aap') || normalized.includes('stage') || 
      normalized.includes('grade') || normalized.includes('extent')) {
    return parseAAPClassification(transcript);
  }
  
  // Check for risk factors
  if (normalized.includes('risk factor')) {
    return parseRiskFactor(transcript);
  }
  
  // Check for referral
  if (normalized.includes('referral')) {
    return parseReferral(transcript);
  }
  
  // Check for mobility (before site readings since it also mentions tooth)
  if (normalized.includes('mobility')) {
    return parseMobility(transcript);
  }
  
  // Check for furcation (before site readings since it also mentions tooth)
  if (normalized.includes('furcation')) {
    return parseFurcation(transcript);
  }
  
  // Check for tooth status
  const toothStatus = parseToothStatus(transcript);
  if (toothStatus) {
    return toothStatus;
  }
  
  // Default to site readings
  const siteReading = parsePerioVoiceInput(transcript);
  if (siteReading) {
    return { ...siteReading, type: 'site_readings' };
  }
  
  return null;
}

/**
 * Parse clinical notes from voice input
 * Examples:
 * - "clinical notes: patient shows signs of gingivitis"
 * - "notes: bleeding observed on lower anterior teeth"
 */
export function parseClinicalNotes(transcript: string): ParsedClinicalNotes | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Match "clinical notes:" or "notes:" followed by text
  const match = normalized.match(/(?:clinical\s+)?notes?\s*[:]\s*(.+)/i);
  
  if (match && match[1]) {
    return {
      type: 'clinical_notes',
      notes: match[1].trim(),
    };
  }
  
  return null;
}

/**
 * Parse AAP classification from voice input
 * Examples:
 * - "AAP stage 2" or "stage 2"
 * - "AAP grade B" or "grade B"
 * - "AAP extent generalized" or "extent localized"
 */
export function parseAAPClassification(transcript: string): ParsedAAPClassification | null {
  const normalized = transcript.toLowerCase().trim();
  const result: ParsedAAPClassification = { type: 'aap_classification' };
  let foundAny = false;
  
  // Parse stage (I, II, III, IV or 1, 2, 3, 4)
  const stageMatch = normalized.match(/stage\s+([1-4]|i{1,4}|one|two|three|four)/i);
  if (stageMatch) {
    const stageValue = stageMatch[1].toLowerCase();
    // Convert to Roman numerals
    const stageMap: Record<string, string> = {
      '1': 'Stage I', 'i': 'Stage I', 'one': 'Stage I',
      '2': 'Stage II', 'ii': 'Stage II', 'two': 'Stage II',
      '3': 'Stage III', 'iii': 'Stage III', 'three': 'Stage III',
      '4': 'Stage IV', 'iv': 'Stage IV', 'four': 'Stage IV',
    };
    result.stage = stageMap[stageValue] || `Stage ${stageValue.toUpperCase()}`;
    foundAny = true;
  }
  
  // Parse grade (A, B, C)
  const gradeMatch = normalized.match(/grade\s+([abc])/i);
  if (gradeMatch) {
    result.grade = `Grade ${gradeMatch[1].toUpperCase()}`;
    foundAny = true;
  }
  
  // Parse extent (localized, generalized)
  const extentMatch = normalized.match(/extent\s+(localized|generalized)/i);
  if (extentMatch) {
    result.extent = extentMatch[1].charAt(0).toUpperCase() + extentMatch[1].slice(1);
    foundAny = true;
  }
  
  return foundAny ? result : null;
}

/**
 * Parse risk factors from voice input
 * Examples:
 * - "risk factor smoking" or "add risk factor diabetes"
 * - "remove risk factor stress"
 */
export function parseRiskFactor(transcript: string): ParsedRiskFactor | null {
  const normalized = transcript.toLowerCase().trim();
  
  const riskFactors = [
    'smoking', 'diabetes', 'genetics', 'stress', 
    'medications', 'poor nutrition', 'poor_nutrition'
  ];
  
  // Check if we're adding or removing
  const isRemoving = normalized.includes('remove') || normalized.includes('delete');
  
  // Find which risk factor is mentioned
  for (const factor of riskFactors) {
    if (normalized.includes(factor)) {
      // Normalize factor name
      let factorKey = factor.replace(/\s+/g, '_') as ParsedRiskFactor['factor'];
      
      return {
        type: 'risk_factor',
        factor: factorKey,
        enabled: !isRemoving,
      };
    }
  }
  
  return null;
}

/**
 * Parse referral information from voice input
 * Examples:
 * - "referral recommended"
 * - "referral reason: severe bone loss"
 * - "no referral" or "remove referral"
 */
export function parseReferral(transcript: string): ParsedReferral | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Check if referral is being removed
  if (normalized.includes('no referral') || normalized.includes('remove referral')) {
    return {
      type: 'referral',
      recommended: false,
    };
  }
  
  // Check for referral reason
  const reasonMatch = normalized.match(/referral\s+(?:reason|because)\s*[:]\s*(.+)/i);
  if (reasonMatch) {
    return {
      type: 'referral',
      recommended: true,
      reason: reasonMatch[1].trim(),
    };
  }
  
  // Check for simple referral recommended
  if (normalized.includes('referral recommended') || normalized.includes('refer to periodontist')) {
    return {
      type: 'referral',
      recommended: true,
    };
  }
  
  return null;
}

/**
 * Parse tooth status from voice input
 * Examples:
 * - "tooth 14 missing"
 * - "tooth 8 implant"
 * - "tooth 5 crown"
 * - "tooth 12 pontic"
 * - "tooth 1 partially impacted"
 * - "tooth 16 fully impacted"
 */
export function parseToothStatus(transcript: string): ParsedToothStatus | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Extract tooth number
  const toothMatch = normalized.match(/tooth\s*(\d+)/i);
  const toothNumber = toothMatch ? parseInt(toothMatch[1]) : null;
  
  if (!toothNumber || toothNumber < 1 || toothNumber > 32) {
    return null;
  }
  
  // Check for status keywords
  const statusMap: Record<string, ParsedToothStatus['status']> = {
    'missing': 'missing',
    'implant': 'implant',
    'crown': 'crown',
    'pontic': 'pontic',
    'bridge': 'pontic', // Alternative term
    'partially impacted': 'partially_impacted',
    'partial impacted': 'partially_impacted',
    'partly impacted': 'partially_impacted',
    'fully impacted': 'fully_impacted',
    'full impacted': 'fully_impacted',
    'completely impacted': 'fully_impacted',
    'present': 'present',
  };
  
  // Check each status keyword
  for (const [keyword, status] of Object.entries(statusMap)) {
    if (normalized.includes(keyword)) {
      return {
        type: 'tooth_status',
        toothNumber,
        status,
      };
    }
  }
  
  return null;
}

/**
 * Parse mobility from voice input
 * Examples:
 * - "tooth 14 mobility 2"
 * - "mobility 1 on tooth 8"
 * - "tooth 21 mobility zero" or "tooth 21 no mobility"
 */
export function parseMobility(transcript: string): ParsedMobility | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Extract tooth number
  const toothMatch = normalized.match(/tooth\s*(\d+)/i);
  const toothNumber = toothMatch ? parseInt(toothMatch[1]) : null;
  
  if (!toothNumber || toothNumber < 1 || toothNumber > 32) {
    return null;
  }
  
  // Check for "no mobility" or "mobility zero"
  if (normalized.includes('no mobility') || normalized.includes('mobility zero')) {
    return {
      type: 'mobility',
      toothNumber,
      mobility: null,
    };
  }
  
  // Extract mobility value (0-3)
  const mobilityMatch = normalized.match(/mobility\s*(\d+|zero|one|two|three)/i);
  
  if (mobilityMatch) {
    const valueStr = mobilityMatch[1].toLowerCase();
    const valueMap: Record<string, number> = {
      'zero': 0, '0': 0,
      'one': 1, '1': 1,
      'two': 2, '2': 2,
      'three': 3, '3': 3,
    };
    
    const mobility = valueMap[valueStr];
    
    if (mobility !== undefined && mobility >= 0 && mobility <= 3) {
      return {
        type: 'mobility',
        toothNumber,
        mobility: mobility === 0 ? null : mobility,
      };
    }
  }
  
  return null;
}

/**
 * Parse furcation from voice input
 * Examples:
 * - "tooth 14 furcation 2"
 * - "furcation 1 on tooth 3"
 * - "tooth 30 furcation zero" or "tooth 30 no furcation"
 */
export function parseFurcation(transcript: string): ParsedFurcation | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Extract tooth number
  const toothMatch = normalized.match(/tooth\s*(\d+)/i);
  const toothNumber = toothMatch ? parseInt(toothMatch[1]) : null;
  
  if (!toothNumber || toothNumber < 1 || toothNumber > 32) {
    return null;
  }
  
  // Check for "no furcation" or "furcation zero"
  if (normalized.includes('no furcation') || normalized.includes('furcation zero')) {
    return {
      type: 'furcation',
      toothNumber,
      furcation: null,
    };
  }
  
  // Extract furcation value (0-3)
  const furcationMatch = normalized.match(/furcation\s*(\d+|zero|one|two|three)/i);
  
  if (furcationMatch) {
    const valueStr = furcationMatch[1].toLowerCase();
    const valueMap: Record<string, number> = {
      'zero': 0, '0': 0,
      'one': 1, '1': 1,
      'two': 2, '2': 2,
      'three': 3, '3': 3,
    };
    
    const furcation = valueMap[valueStr];
    
    if (furcation !== undefined && furcation >= 0 && furcation <= 3) {
      return {
        type: 'furcation',
        toothNumber,
        furcation: furcation === 0 ? null : furcation,
      };
    }
  }
  
  return null;
}

/**
 * Parse voice input for periodontal readings.
 * Supports formats like:
 * - "tooth 14 MB 3 B 2 DB 4"
 * - "fourteen mesial buccal 3 buccal 2"
 * - "3 2 4 bleeding on MB"
 */
export function parsePerioVoiceInput(transcript: string): ParsedPerioReading | null {
  const normalized = transcript.toLowerCase().trim();
  
  // Extract tooth number
  const toothMatch = normalized.match(/tooth\s*(\d+)|(\d+)\s*(?:mb|b|db|ml|l|dl)/i);
  const toothNumber = toothMatch ? parseInt(toothMatch[1] || toothMatch[2]) : null;
  
  if (!toothNumber || toothNumber < 1 || toothNumber > 32) {
    return null;
  }

  const readings: ParsedPerioReading['readings'] = [];
  
  // Site mappings
  const sitePatterns: Record<string, string> = {
    'mb': 'MB',
    'mesial buccal': 'MB',
    'mesiobuccal': 'MB',
    'b': 'B',
    'buccal': 'B',
    'db': 'DB',
    'distal buccal': 'DB',
    'distobuccal': 'DB',
    'ml': 'ML',
    'mesial lingual': 'ML',
    'mesiolingual': 'ML',
    'l': 'L',
    'lingual': 'L',
    'dl': 'DL',
    'distal lingual': 'DL',
    'distolingual': 'DL',
  };

  // Parse site readings
  for (const [pattern, siteCode] of Object.entries(sitePatterns)) {
    const regex = new RegExp(`${pattern}\\s*(\\d+)?`, 'gi');
    const matches = normalized.matchAll(regex);
    
    for (const match of matches) {
      const depth = match[1] ? parseInt(match[1]) : undefined;
      const bleeding = normalized.includes(`bleeding on ${pattern}`) || 
                       normalized.includes(`bleeding ${pattern}`) ||
                       normalized.includes(`${pattern} bleeding`);
      
      readings.push({
        site: siteCode,
        pocketDepth: depth,
        bleeding,
      });
    }
  }

  // Parse sequential numbers (e.g., "3 2 4 3 2 5")
  const numberSequence = normalized.match(/(\d+(?:\s+\d+){5})/);
  if (numberSequence && readings.length === 0) {
    const numbers = numberSequence[1].split(/\s+/).map(Number);
    const sites = ['MB', 'B', 'DB', 'ML', 'L', 'DL'];
    
    numbers.forEach((num, index) => {
      if (index < sites.length) {
        readings.push({
          site: sites[index],
          pocketDepth: num,
        });
      }
    });
  }

  if (readings.length === 0) {
    return null;
  }

  return {
    toothNumber,
    readings,
  };
}

/**
 * Parse tooth number from spoken text.
 */
export function parseSpokenToothNumber(text: string): number | null {
  const normalized = text.toLowerCase().trim();
  
  // Direct number
  const directMatch = normalized.match(/\b(\d{1,2})\b/);
  if (directMatch) {
    const num = parseInt(directMatch[1]);
    if (num >= 1 && num <= 32) return num;
  }
  
  // Spoken numbers
  const spokenNumbers: Record<string, number> = {
    'one': 1, 'two': 2, 'three': 3, 'four': 4, 'five': 5,
    'six': 6, 'seven': 7, 'eight': 8, 'nine': 9, 'ten': 10,
    'eleven': 11, 'twelve': 12, 'thirteen': 13, 'fourteen': 14,
    'fifteen': 15, 'sixteen': 16, 'seventeen': 17, 'eighteen': 18,
    'nineteen': 19, 'twenty': 20, 'twenty one': 21, 'twenty two': 22,
    'twenty three': 23, 'twenty four': 24, 'twenty five': 25,
    'twenty six': 26, 'twenty seven': 27, 'twenty eight': 28,
    'twenty nine': 29, 'thirty': 30, 'thirty one': 31, 'thirty two': 32,
  };
  
  for (const [word, num] of Object.entries(spokenNumbers)) {
    if (normalized.includes(word)) {
      return num;
    }
  }
  
  return null;
}
